// @ts-check
import ZeroDataGrid from '~/components/Shared/ZeroDataGrid/ZeroDataGrid';
import { formatName } from '~/other/Helper';
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMembersDataGridContext } from '~/components/AdminPages/MembersDataGrid/MembersDataGrid.context';
import { DEFAULT_SORT_MODEL, EventNames, getColumnDefs, loadUsers, MAX_USER_ROWS } from '~/components/AdminPages/MembersDataGrid/MembersDataGrid.utils';
import { useClientSideDataGridSetup } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';
import { useLocation } from 'react-router';

const USERS_PER_PAGE = 50;

/**
 * @param {{
 *  members: any[],
 *  handleMenuClick: (key: string, user: any) => void,
 *  generateMenuOptions: (user: any) => any[],
 *  setDisplayedRowCount: (rowCount: number) => void,
 * }} props
 */
export default function MembersDataGrid({ 
    members,
    handleMenuClick,
    generateMenuOptions,
    setDisplayedRowCount,
}) {
    const { gridRef, rowCount, activeModal, setActiveModal, setSettings } = useMembersDataGridContext();
    const colDefs = useMemo(getColumnDefs, []);
    const location = useLocation();
    const { onStateUpdated } = useClientSideDataGridSetup({
        eventNames: EventNames,
        gridRef,
        colDefs,
        defaultSortModel: DEFAULT_SORT_MODEL,
        setSettings,
    });

    useEffect(() => {
        if (activeModal) {
            handleMenuClick(activeModal.key, activeModal.user);
            setActiveModal(null);
        }
    }, [handleMenuClick, activeModal, setActiveModal]);

    const membersWithMenuOptions = useMemo(() => {
        return members.map(member => ({
            ...member,
            menuOptions: generateMenuOptions(member),
        }));
    }, [members, generateMenuOptions]);

    const onModelUpdated = useCallback((/** @type {AgGrid.ModelUpdatedEvent} */ event) => {
        setDisplayedRowCount(event.api.getDisplayedRowCount());
    }, [setDisplayedRowCount]);

    return (
        <>
            <ZeroDataGrid
                clientSide={true}
                gridRef={gridRef}
                onStateUpdated={onStateUpdated}
                onModelUpdated={onModelUpdated}
                initialState={location.state?.gridState ?? null}
                columnDefs={colDefs}
                defaultColDef={{
                    cellStyle: {
                        display: 'flex',
                        alignItems: 'center',
                    }
                }}
                rowData={membersWithMenuOptions}
                pagination={true}
                paginationPageSizeSelector={false}
                paginationPageSize={rowCount ?? USERS_PER_PAGE}
                tooltipShowDelay={500}
                tooltipShowMode="standard"
                suppressCellFocus
                enableCellTextSelection
                ensureDomOrder
            />
        </>
    );
}
